import { Icon } from '@iconify/react'
import { useMemo, useState } from 'react'
import {
  filterDonorsByRange,
  filterDonorsBySearch,
  formatCurrency,
  formatDateTime,
  formatNumber,
  sortDonors,
  type DonorSortKey,
} from '@/lib/campaign-dashboard'
import type { CampaignDonorsResponse } from '@/types/campaign-report'
import styles from './CampaignDashboard.module.css'

interface CampaignDonorsTableProps {
  data: CampaignDonorsResponse | null
  isLoading: boolean
  error: string | null
  onRetry: () => void
  dateFrom: string
  dateTo: string
  page: number
  onPageChange: (page: number) => void
}

function SortIndicator({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) {
  if (!active) return <Icon icon="mdi:swap-vertical" />
  return <Icon icon={direction === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down'} />
}

export function CampaignDonorsTable({
  data,
  isLoading,
  error,
  onRetry,
  dateFrom,
  dateTo,
  page,
  onPageChange,
}: CampaignDonorsTableProps) {
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<DonorSortKey>('last_payment_paid_at')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  const filteredRows = useMemo(() => {
    if (!data) return []
    const ranged = filterDonorsByRange(data.donors, dateFrom, dateTo)
    const searched = filterDonorsBySearch(ranged, search)
    return sortDonors(searched, sortKey, sortDirection)
  }, [data, dateFrom, dateTo, search, sortDirection, sortKey])

  const highlights = useMemo(() => {
    const topDonor = [...filteredRows].sort(
      (left, right) => right.total_amount_in_campaign - left.total_amount_in_campaign
    )[0]
    const recurrentDonor = [...filteredRows].sort(
      (left, right) => right.payments_count_in_campaign - left.payments_count_in_campaign
    )[0]
    const recurrentCount = filteredRows.filter(
      (donor) => donor.payments_count_in_campaign > 1
    ).length

    return { topDonor, recurrentDonor, recurrentCount }
  }, [filteredRows])

  const pagination = data?.pagination

  const toggleSort = (key: DonorSortKey) => {
    if (key === sortKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(key)
    setSortDirection('desc')
  }

  return (
    <section className={styles.tableCard}>
      <div className={styles.tableHeader}>
        <div>
          <h2 className={styles.sectionTitle}>Compradores</h2>
          <p className={styles.sectionSubtitle}>
            Pagina retornada pelo backend com filtro visual local no periodo selecionado.
          </p>
        </div>
      </div>

      <div className={styles.tableToolbar}>
        <div className={styles.tableHighlights}>
          <span className={styles.badgeWarning}>
            <Icon icon="mdi:filter-outline" />
            Filtro local nesta pagina
          </span>
          <span className={styles.badgeInfo}>
            <Icon icon="mdi:trophy-outline" />
            Maior comprador:{' '}
            {highlights.topDonor
              ? `${highlights.topDonor.name} (${formatCurrency(highlights.topDonor.total_amount_in_campaign)})`
              : 'Sem dados'}
          </span>
          <span className={styles.badgeInfo}>
            <Icon icon="mdi:repeat" />
            Recorrentes: {formatNumber(highlights.recurrentCount)}
          </span>
        </div>

        <div className={styles.tableSearchWrap}>
          <input
            type="search"
            className={`${styles.searchInput} ${styles.tableSearch}`}
            placeholder="Buscar por nome, email ou telefone"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      {highlights.recurrentDonor && (
        <div className={styles.tableMeta}>
          Mais recorrente nesta pagina: {highlights.recurrentDonor.name} ·{' '}
          {formatNumber(highlights.recurrentDonor.payments_count_in_campaign)} pagamentos
        </div>
      )}

      {isLoading ? (
        <div className={styles.loadingCard}>
          <div className={styles.skeletonGrid}>
            <div className={styles.skeletonBlock} />
          </div>
        </div>
      ) : error ? (
        <div className={styles.errorState} role="alert">
          <span className={styles.stateIcon}>
            <Icon icon="mdi:alert-circle-outline" />
          </span>
          <h3 className={styles.stateTitle}>Erro ao carregar compradores</h3>
          <p className={styles.stateText}>{error}</p>
          <button type="button" className={styles.actionButton} onClick={onRetry}>
            <Icon icon="mdi:refresh" />
            Tentar novamente
          </button>
        </div>
      ) : (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>
                    <button type="button" className={styles.sortableHeader} onClick={() => toggleSort('name')}>
                      Nome
                      <SortIndicator active={sortKey === 'name'} direction={sortDirection} />
                    </button>
                  </th>
                  <th>
                    <button type="button" className={styles.sortableHeader} onClick={() => toggleSort('email')}>
                      Email
                      <SortIndicator active={sortKey === 'email'} direction={sortDirection} />
                    </button>
                  </th>
                  <th>Telefone</th>
                  <th>
                    <button type="button" className={styles.sortableHeader} onClick={() => toggleSort('last_payment_paid_at')}>
                      Ultimo pagamento
                      <SortIndicator active={sortKey === 'last_payment_paid_at'} direction={sortDirection} />
                    </button>
                  </th>
                  <th>
                    <button type="button" className={styles.sortableHeader} onClick={() => toggleSort('last_payment_amount')}>
                      Ultimo valor
                      <SortIndicator active={sortKey === 'last_payment_amount'} direction={sortDirection} />
                    </button>
                  </th>
                  <th>
                    <button type="button" className={styles.sortableHeader} onClick={() => toggleSort('total_amount_in_campaign')}>
                      Total na campanha
                      <SortIndicator active={sortKey === 'total_amount_in_campaign'} direction={sortDirection} />
                    </button>
                  </th>
                  <th>
                    <button type="button" className={styles.sortableHeader} onClick={() => toggleSort('payments_count_in_campaign')}>
                      Pagamentos
                      <SortIndicator active={sortKey === 'payments_count_in_campaign'} direction={sortDirection} />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className={styles.emptyState}>
                        <span className={styles.emptyIcon}>
                          <Icon icon="mdi:account-search-outline" />
                        </span>
                        <h3 className={styles.stateTitle}>Nenhum comprador visivel nesta pagina</h3>
                        <p className={styles.stateText}>
                          Ajuste a busca ou o periodo para ver os itens da pagina atual.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((donor) => (
                    <tr key={donor.donor_id}>
                      <td><div className={styles.tableName}>{donor.name}</div></td>
                      <td><span className={styles.tableMuted}>{donor.email || '-'}</span></td>
                      <td><span className={styles.tableMuted}>{donor.phone || '-'}</span></td>
                      <td>{formatDateTime(donor.last_payment_paid_at)}</td>
                      <td>{formatCurrency(donor.last_payment_amount)}</td>
                      <td>{formatCurrency(donor.total_amount_in_campaign)}</td>
                      <td>{formatNumber(donor.payments_count_in_campaign)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.paginationButton}
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              >
                <Icon icon="mdi:chevron-left" />
                Anterior
              </button>
              <span className={styles.paginationInfo}>
                Pagina {pagination.current_page} de {pagination.last_page} · Total: {formatNumber(pagination.total)}
              </span>
              <button
                type="button"
                className={styles.paginationButton}
                disabled={page >= pagination.last_page}
                onClick={() => onPageChange(page + 1)}
              >
                Proxima
                <Icon icon="mdi:chevron-right" />
              </button>
            </div>
          )}
        </>
      )}
    </section>
  )
}
